import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock, LogOut } from 'lucide-react';

const IDLE_WARNING_TIME = 29 * 60 * 1000; // 29 minutes in ms — warn 1 minute before logout
const IDLE_LOGOUT_TIME = 30 * 60 * 1000;  // 30 minutes in ms
const COUNTDOWN_START = 60; // 60 seconds countdown

/**
 * IdleTimeout Component
 * 
 * Monitors user activity and:
 * - Shows warning modal at 29 minutes of inactivity
 * - Auto-logs out at 30 minutes of inactivity
 * - Countdown timer in warning modal
 * - "Stay Signed In" button to reset timer
 */
export default function IdleTimeout() {
  const { isAuthenticated, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_START);
  
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset all timers
  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    // Write last activity to localStorage so heartbeat can detect idle state
    localStorage.setItem('lastUserActivity', String(Date.now()));
    setShowWarning(false);
    setCountdown(COUNTDOWN_START);
    
    // Clear existing timers
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Only set timers if authenticated
    if (!isAuthenticated) return;
    
    // Set warning timer (10 minutes)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(COUNTDOWN_START);
      
      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, IDLE_WARNING_TIME);
    
    // Set logout timer (11 minutes)
    logoutTimerRef.current = setTimeout(() => {
      logout();
    }, IDLE_LOGOUT_TIME);
  }, [isAuthenticated, logout]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Only reset if warning is not showing
    if (!showWarning) {
      resetTimers();
    }
  }, [resetTimers, showWarning]);

  // Stay signed in - reset timers
  const handleStaySignedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Manual logout from warning
  const handleLogout = useCallback(() => {
    setShowWarning(false);
    logout();
  }, [logout]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle activity handler to avoid excessive resets
    let throttleTimer = null;
    const throttledActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        handleActivity();
        throttleTimer = null;
      }, 1000); // Throttle to once per second
    };

    events.forEach(event => {
      document.addEventListener(event, throttledActivity, { passive: true });
    });

    // Initial timer setup
    resetTimers();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledActivity);
      });
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [isAuthenticated, handleActivity, resetTimers]);

  // Don't render if not authenticated
  if (!isAuthenticated) return null;

  return (
    <AlertDialog open={showWarning} onOpenChange={() => {}}>
      <AlertDialogContent className="max-w-md" data-testid="idle-timeout-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Session Timeout Warning
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                You've been inactive for 29 minutes. For security, you'll be automatically logged out soon.
              </p>
              <div className="flex items-center justify-center">
                <div className="text-4xl font-bold text-amber-600 bg-amber-50 px-6 py-3 rounded-lg" data-testid="countdown-timer">
                  {countdown}s
                </div>
              </div>
              <p className="text-center text-sm text-muted-foreground">
                Click "Stay Signed In" to continue your session
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleLogout} data-testid="logout-now-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Log Out Now
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleStaySignedIn} data-testid="stay-signed-in-btn">
            Stay Signed In
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
